import { useObserver } from 'mobx-react';
import { useRouter } from 'next/compat/router';

import { Product } from 'types/graphql-overrides';
import type { Maybe, CollectionCardBadge } from 'src/gql/graphql';
import useCart from 'src/hooks/use-cart';
import usePaths from 'src/hooks/use-paths';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useHideDiscountFromConsumer } from 'shared/hooks/use-hide-discounts-from-consumer';
import { useShowTotalTerpenes } from 'shared/hooks/use-show-total-terpenes';
import { getPotencyStrings } from 'src/utils/helpers/product';
import { productIsPartOfActiveSpecial, isNotSolitaryMinimumSpendOffer } from 'shared/helpers/specials';
import { useProductPricing } from 'src/hooks/use-product-pricing';
import { useHideSalesLanguage } from 'src/hooks/use-hide-sales-language';

type UseCardProps = {
  product: Product;
};

type UseCardReturn = {
  href: string;
  isStaffPick: boolean;
  isSponsored: boolean;
  brandName: string;
  strainType: Maybe<string> | undefined;
  potency: string[];
  isSpecialOffer: boolean;
  specialNames: string[];
  hideDiscount: boolean;
  hideSalesLanguage: boolean;
  priceOptions: ReturnType<ReturnType<typeof useProductPricing>['getPriceOptions']>;
  collectionCardBadge: CollectionCardBadge | null;
};

export const useCard = ({ product }: UseCardProps): UseCardReturn => {
  const { href } = usePaths({
    product,
  });

  const isStaffPick = !!product.featured?.current;
  const isSponsored = !!product.adTrackers;
  const brandName = product.brand.name;
  const strainType = product.strainType !== 'N/A' ? product.strainType : null;

  const { dispensary } = useDispensary();
  const showTotalTerpenes = useShowTotalTerpenes(dispensary);
  const posDiscountNames = (product.posDiscountNames ?? []).filter((name): name is string => Boolean(name));
  // posDiscountNames is only populated server-side when the dpos-menu-pricing flag is on (variants 2/3)
  // and the integration has discount sync enabled -- a non-empty array is already the gate.
  const hasPOSDiscounts = posDiscountNames.length > 0;

  const productForPotencyHelpers = product.thcContent
    ? { ...product, THCContent: product.thcContent, CBDContent: product.cbdContent }
    : product;
  const { THC: THCString, CBD: CBDString, TAC: TACString, TERPS: TERPSString } = getPotencyStrings(
    productForPotencyHelpers
  );
  const showTAC = !!product.cannabinoidsV2?.length;
  const potency: string[] = [];

  if (showTAC && typeof TACString === 'string') {
    potency.push(`TAC: ${TACString}`);
  }

  if (typeof THCString === 'string') {
    potency.push(`THC: ${THCString}`);
  }

  if (typeof CBDString === 'string') {
    potency.push(`CBD: ${CBDString}`);
  }

  if (showTotalTerpenes && typeof TERPSString === 'string') {
    potency.push(`TERPS: ${TERPSString}`);
  }

  const { query: routerQuery = {} } = useRouter() ?? {};
  const Cart = useCart();
  const menuType = useObserver(() => Cart.menuType);
  const hideDiscount = useHideDiscountFromConsumer(dispensary);
  const hideSalesLanguage = useHideSalesLanguage();
  const isInBogo = productIsPartOfActiveSpecial(product, menuType);
  const isSpecialOffer =
    !routerQuery.specialId &&
    !hideDiscount &&
    (hasPOSDiscounts || (isInBogo && isNotSolitaryMinimumSpendOffer(product.specialData?.bogoSpecials)));

  const specialNames = hasPOSDiscounts
    ? posDiscountNames
    : (product.specialData?.bogoSpecials ?? [])
        .filter((special) => special?.qualifyingOptions && special.qualifyingOptions.length > 0)
        .map((special) => special?.menuDisplayName ?? special?.specialName)
        .filter((name): name is string => Boolean(name));

  const { getPriceOptions } = useProductPricing();
  const priceOptions = getPriceOptions(product);

  const collectionCardBadge = product.collectionCardBadge ?? null;

  return {
    href,
    isStaffPick,
    isSponsored,
    brandName,
    strainType,
    potency,
    isSpecialOffer,
    specialNames,
    hideDiscount,
    hideSalesLanguage,
    priceOptions,
    collectionCardBadge,
  };
};
